import React, { useState } from 'react';
import { useLinks } from '@/hooks/use-links';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, GripVertical, Sparkles } from 'lucide-react';
import { LinkCard } from './LinkCard';
import { SocialTemplates, socialPlatforms } from './SocialTemplates';
import { ColorPalette } from './ColorPalette';
import { CreateLinkData, Link, SocialPlatform } from '@/types/link-short';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

interface LinksManagerProps {
  mainLinkId: string;
}

const linkStyles = [
  { value: 'default', label: 'Padrão' },
  { value: 'pill', label: 'Pílula' },
  { value: 'square', label: 'Quadrado' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'gradient', label: 'Gradiente' },
  { value: 'glass', label: 'Vidro' }
];

export function LinksManager({ mainLinkId }: LinksManagerProps) {
  const { links, createLink, updateLink, deleteLink, reorderLinks } = useLinks(mainLinkId);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLink, setEditingLink] = useState<Link | null>(null);
  const [formData, setFormData] = useState<CreateLinkData>({
    title: '',
    url: '',
    icon: '',
    order_index: 0,
    link_style: 'default',
    icon_position: 'left'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingLink) {
      // Para atualização, convertemos os dados para o formato correto
      const updateData: Partial<Link> = {
        title: formData.title,
        url: formData.url,
        icon: formData.icon,
        bg_color: formData.bg_color,
        text_color: formData.text_color,
        link_style: formData.link_style as 'default' | 'pill' | 'square' | 'minimal' | 'gradient' | 'glass',
        icon_position: formData.icon_position as 'left' | 'right' | 'top' | 'none',
        social_platform: formData.social_platform
      };
      await updateLink(editingLink.id, updateData);
      setEditingLink(null);
    } else {
      await createLink({
        ...formData,
        order_index: links.length
      });
      setShowAddForm(false);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      icon: '',
      order_index: 0,
      link_style: 'default',
      icon_position: 'left'
    });
  };

  const handleEdit = (link: Link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
      icon: link.icon || '',
      order_index: link.order_index,
      bg_color: link.bg_color,
      text_color: link.text_color,
      link_style: link.link_style || 'default',
      icon_position: link.icon_position || 'left',
      social_platform: link.social_platform
    });
    setShowAddForm(true);
  };

  const handleDelete = async (linkId: string) => {
    if (confirm('Tem certeza que deseja excluir este link?')) {
      await deleteLink(linkId);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(links);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const orderedIds = items.map(item => item.id);
    reorderLinks(orderedIds);
  };

  const handleSocialSelect = (platform: SocialPlatform) => {
    setFormData(prev => ({
      ...prev,
      title: platform.name,
      url: platform.placeholder,
      icon: platform.icon,
      bg_color: platform.color,
      text_color: '#ffffff',
      social_platform: platform.value
    }));
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingLink(null);
    resetForm();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Gerenciar Links</h3>
          <p className="text-sm text-gray-600">
            {links.length} {links.length === 1 ? 'link adicionado' : 'links adicionados'}
          </p>
        </div>
        <Button onClick={() => setShowAddForm(true)} size="sm" className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
          <Plus className="h-4 w-4 mr-1" />
          Adicionar Link
        </Button>
      </div>

      {showAddForm && (
        <Card className="border-2 border-blue-200 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-purple-500" />
              {editingLink ? 'Editar Link' : 'Novo Link'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Básico</TabsTrigger>
                <TabsTrigger value="social">Redes Sociais</TabsTrigger>
                <TabsTrigger value="style">Estilo</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit}>
                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="link-title">Título *</Label>
                      <Input
                        id="link-title"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Meu Instagram"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="link-url">URL *</Label>
                      <Input
                        id="link-url"
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://instagram.com/meuusuario"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="link-icon">Ícone</Label>
                    <Input
                      id="link-icon"
                      value={formData.icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="Digite um emoji ou ícone"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="social" className="space-y-4 mt-4">
                  <SocialTemplates onSelect={handleSocialSelect} />
                </TabsContent>

                <TabsContent value="style" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Estilo do Botão</Label>
                        <Select
                          value={formData.link_style || 'default'}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, link_style: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {linkStyles.map((style) => (
                              <SelectItem key={style.value} value={style.value}>
                                {style.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Posição do Ícone</Label>
                        <Select
                          value={formData.icon_position || 'left'}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, icon_position: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Esquerda</SelectItem>
                            <SelectItem value="right">Direita</SelectItem>
                            <SelectItem value="none">Sem ícone</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <ColorPalette
                        label="Cor de Fundo"
                        value={formData.bg_color || '#3B82F6'}
                        onChange={(value) => setFormData(prev => ({ ...prev, bg_color: value }))}
                        showGradients={true}
                      />

                      <ColorPalette
                        label="Cor do Texto"
                        value={formData.text_color || '#FFFFFF'}
                        onChange={(value) => setFormData(prev => ({ ...prev, text_color: value }))}
                      />
                    </div>
                  </div>
                </TabsContent>

                <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={cancelForm}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                    {editingLink ? 'Salvar Alterações' : 'Adicionar Link'}
                  </Button>
                </div>
              </form>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="links">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
              {links.map((link, index) => (
                <Draggable key={link.id} draggableId={link.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`flex items-center gap-3 ${snapshot.isDragging ? 'z-50' : ''}`}
                    >
                      <div {...provided.dragHandleProps} className="cursor-grab hover:cursor-grabbing">
                        <GripVertical className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <LinkCard
                          link={link}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {links.length === 0 && !showAddForm && (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Plus className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum link adicionado</h3>
            <p className="text-gray-500 text-center mb-4">
              Comece adicionando seus primeiros links ou use os templates de redes sociais.
            </p>
            <Button onClick={() => setShowAddForm(true)} className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Primeiro Link
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
